import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminLogin, formatApiErrorDetail } from "@/lib/adminApi";
import { Sparkles, LogIn } from "lucide-react";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await adminLogin(email.trim(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      className="min-h-screen flex items-center justify-center px-6 py-10"
      style={{ background: "#030712" }}
    >
      <div className="orb orb-blue" style={{ width: 600, height: 600, top: "-200px", left: "-200px" }} />
      <div className="orb orb-cyan" style={{ width: 480, height: 480, bottom: "-180px", right: "-160px" }} />

      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="relative z-10 w-full max-w-md rounded-2xl p-8"
        style={{
          background: "rgba(11,17,32,0.85)",
          border: "1px solid rgba(255,255,255,0.1)",
          backdropFilter: "blur(20px)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
        }}
      >
        <div className="flex items-center gap-3 mb-8">
          <div
            className="h-10 w-10 rounded-md flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, #D4AF37 0%, #F3CE5E 100%)",
            }}
          >
            <Sparkles size={18} className="text-[#030712]" />
          </div>
          <div>
            <div className="font-display font-black text-white text-lg leading-none">
              FAVOR <span className="text-[#D4AF37]">STUDIO</span>
            </div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-cyan-300/80 mt-1">
              Admin Console
            </div>
          </div>
        </div>

        <h1 className="font-display text-3xl font-black text-white leading-tight mb-2">
          Sign in.
        </h1>
        <p className="text-white/55 text-sm mb-8">
          Restricted to the Always On Sales operator.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-cyan-300/80 mb-2">
              Email
            </label>
            <input
              data-testid="admin-email"
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-dark rounded-lg"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-cyan-300/80 mb-2">
              Password
            </label>
            <input
              data-testid="admin-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-dark rounded-lg"
              placeholder="••••••••"
            />
          </div>
        </div>

        {error && (
          <div
            data-testid="admin-login-error"
            className="mt-5 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2"
          >
            {error}
          </div>
        )}

        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={busy}
          className="btn-gold rounded-md w-full mt-8 justify-center"
        >
          {busy ? "Signing in…" : (<>Sign In <LogIn size={16} /></>)}
        </button>

        <a
          href="/"
          className="block text-center text-xs uppercase tracking-[0.2em] text-white/45 hover:text-white/80 mt-6"
        >
          ← Back to site
        </a>
      </form>
    </main>
  );
}
